import { useCallback, useState } from 'react';
import { amsPublicApiAccountSwitchSwitchAccount } from 'service/amsApi';
import type { ErrorResponse } from 'connectors/ams';
import { showNotification } from 'common/function';
import { reportError } from 'utils/reporting';

type SwitchResult = 'switched' | 'refused';

type UseAccountSwitchReturn = {
  switchAccount: (accountCode: string) => Promise<SwitchResult>;
  isSwitching: boolean;
};

const TARGET_UNAVAILABLE = 'That account is no longer available to you. Refresh to see the current list.';
const NOT_READY = "Switching accounts isn't available right now. Contact support if this continues.";
const NO_ACCESS = "Switching accounts isn't available for your access. Contact support if this continues.";
const CONTENDED = 'Someone else is changing your access right now. Try again in a moment.';
const SESSION_EXPIRED = 'Your session has expired. Sign in again to switch accounts.';
const GENERIC_FAILURE = 'Could not switch account. Please try again.';

/**
 * Maps a refusal to its message. Split out to keep the caller flat, and because each arm is a
 * distinct server contract rather than a variation on one.
 */
function refuse(error: ErrorResponse): SwitchResult {
  switch (error.code) {
    case 'not_found':
      // 170232 collapses unknown code, missing connection, inactive grant and inactive target
      // routing into one 404 so account codes stay non-enumerable, so this is as specific as the
      // server allows. Target-specific: another account may still work.
      showNotification(TARGET_UNAVAILABLE, 'Account unavailable', 'error');
      break;
    case 'account_not_provisioned':
      // Deliberately does not name the target. `switch-account` also raises this from
      // `resolve_own_active_routing` for the *caller's own* account, so on a return-home call from a
      // deactivated target the offending account is the one being left, not the one being entered.
      showNotification(NOT_READY, 'Switching unavailable', 'error');
      break;
    case 'account_access_denied':
    case 'no_workspace_assigned':
      // The caller has no usable footing: no visitor row, no UserAccountMapping, or no grant on an
      // ACTIVE workspace. Like account_not_provisioned this is not necessarily about the target --
      // NoUserRowError maps here for the account the request resolves against -- so the copy names
      // no account. Distinct from not_found on purpose, and AMS logs the specific cause already.
      showNotification(NO_ACCESS, 'Switching unavailable', 'error');
      break;
    case 'concurrent_modification':
      // The flag write's row lock timed out. Genuinely retriable, unlike every arm above.
      showNotification(CONTENDED, 'Try again', 'error');
      break;
    case 'unauthorized':
      // The re-mint is capped at the presented token's own expiry, so a token near the end of its
      // life cannot authorise a switch. Signing in again is the remedy, not retrying.
      showNotification(SESSION_EXPIRED, 'Session expired', 'error');
      break;
    default:
      reportError(new Error(`Account switch failed: ${error.code}`));
      showNotification(GENERIC_FAILURE, 'Switch failed', 'error');
  }
  return 'refused';
}

/**
 * Switches the AMS session into another account the caller holds an ACTIVE cross-account grant on,
 * or back to their home account by naming it.
 *
 * The switched token is deliberately discarded, for the same reason `useWorkspaceSwitch` discards
 * its own: the switcher reloads the page on success, which throws away the in-memory token store,
 * and the choice is durable server-side. 170232 records it on the caller's grant row as
 * `is_current`, and `POST /auth/token` honours that flag on the next exchange — so the post-reload
 * bootstrap lands in the switched-to account, and the workspace list that follows is that account's.
 *
 * The caller owns the reload — see TenancySwitcher.
 */
export function useAccountSwitch(): UseAccountSwitchReturn {
  const [isSwitching, setIsSwitching] = useState(false);

  const switchAccount = useCallback(async (accountCode: string): Promise<SwitchResult> => {
    setIsSwitching(true);
    try {
      const { error } = await amsPublicApiAccountSwitchSwitchAccount({ body: { account_code: accountCode } });
      return error ? refuse(error) : 'switched';
    } catch (err) {
      reportError(err);
      showNotification(GENERIC_FAILURE, 'Switch failed', 'error');
      return 'refused';
    } finally {
      setIsSwitching(false);
    }
  }, []);

  return { switchAccount, isSwitching };
}
